
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ContractGroup } from "@/lib/types";

// Define types for the raw database rows
interface ContractGroupRow {
  id: string;
  name: string;
  description: string | null;
  user_id: string;
  created_at: string;
  updated_at: string;
}

export function useContractGroups() {
  const queryClient = useQueryClient();
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  const fetchContractGroups = async (): Promise<ContractGroup[]> => {
    // Use explicit type casting for the query
    const { data, error } = await supabase
      .from("contract_groups")
      .select("*")
      .order("name") as { data: ContractGroupRow[] | null, error: any };

    if (error) {
      toast.error(`Error fetching contract groups: ${error.message}`);
      throw error;
    }

    if (!data) return [];

    return data.map(group => ({
      id: group.id,
      name: group.name,
      description: group.description || undefined,
      user_id: group.user_id,
      created_at: group.created_at,
      updated_at: group.updated_at,
      // Add the camelCase versions for convenience
      createdAt: new Date(group.created_at),
      updatedAt: new Date(group.updated_at)
    }));
  };

  const createContractGroup = async (name: string, description: string): Promise<ContractGroup> => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error("User not authenticated");
      throw new Error("User not authenticated");
    }

    // Use explicit type casting for the query
    const { data, error } = await supabase
      .from("contract_groups")
      .insert([
        {
          name,
          description,
          user_id: userId
        }
      ])
      .select()
      .single() as { data: ContractGroupRow | null, error: any };

    if (error) {
      toast.error(`Error creating contract group: ${error.message}`);
      throw error;
    }

    if (!data) {
      toast.error("No data returned when creating contract group");
      throw new Error("No data returned when creating contract group");
    }

    toast.success("Contract group created successfully");

    return {
      id: data.id,
      name: data.name,
      description: data.description || undefined,
      user_id: data.user_id,
      created_at: data.created_at,
      updated_at: data.updated_at,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at)
    };
  };

  const assignContractsToGroup = async (contractIds: string[], groupId: string): Promise<void> => {
    if (contractIds.length === 0) return;

    // Use a type assertion to specify the structure
    type ContractUpdate = { group_id: string | null };
    
    const { error } = await supabase
      .from("contracts")
      .update({ group_id: groupId } as ContractUpdate)
      .in("id", contractIds);

    if (error) {
      toast.error(`Error assigning contracts to group: ${error.message}`);
      throw error;
    }

    toast.success(`${contractIds.length} contracts assigned to group`);
    
    // Invalidate contracts query to refresh the data
    queryClient.invalidateQueries({ queryKey: ["contracts"] });
  };

  const removeContractsFromGroup = async (contractIds: string[]): Promise<void> => {
    if (contractIds.length === 0) return;

    // Use a type assertion to specify the structure
    type ContractUpdate = { group_id: null };
    
    const { error } = await supabase
      .from("contracts")
      .update({ group_id: null } as ContractUpdate)
      .in("id", contractIds);

    if (error) {
      toast.error(`Error removing contracts from group: ${error.message}`);
      throw error;
    }

    toast.success(`${contractIds.length} contracts removed from group`);
    
    // Invalidate contracts query to refresh the data
    queryClient.invalidateQueries({ queryKey: ["contracts"] });
  };

  const deleteContractGroup = async (groupId: string): Promise<void> => {
    // First remove all contracts from this group
    // Use a type assertion to specify the structure
    type ContractUpdate = { group_id: null };
    
    const { error: updateError } = await supabase
      .from("contracts")
      .update({ group_id: null } as ContractUpdate)
      .eq("group_id", groupId);

    if (updateError) {
      toast.error(`Error removing contracts from group: ${updateError.message}`);
      throw updateError;
    }

    // Then delete the group
    const { error } = await supabase
      .from("contract_groups")
      .delete()
      .eq("id", groupId) as { error: any };

    if (error) {
      toast.error(`Error deleting contract group: ${error.message}`);
      throw error;
    }

    toast.success("Contract group deleted successfully");
    
    // Invalidate queries to refresh the data
    queryClient.invalidateQueries({ queryKey: ["contract_groups"] });
    queryClient.invalidateQueries({ queryKey: ["contracts"] });
  };

  const useContractGroupsQuery = () => useQuery({
    queryKey: ["contract_groups"],
    queryFn: fetchContractGroups,
  });

  const useCreateContractGroupMutation = () => useMutation({
    mutationFn: async ({ name, description }: { name: string, description: string }) => {
      setIsCreatingGroup(true);
      try {
        return await createContractGroup(name, description);
      } finally {
        setIsCreatingGroup(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract_groups"] });
    }
  });

  const useAssignContractsToGroupMutation = () => useMutation({
    mutationFn: async ({ contractIds, groupId }: { contractIds: string[], groupId: string }) => {
      return await assignContractsToGroup(contractIds, groupId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
    }
  });

  const useRemoveContractsFromGroupMutation = () => useMutation({
    mutationFn: async (contractIds: string[]) => {
      return await removeContractsFromGroup(contractIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
    }
  });

  const useDeleteContractGroupMutation = () => useMutation({
    mutationFn: deleteContractGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract_groups"] });
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
    }
  });

  return {
    isCreatingGroup,
    useContractGroupsQuery,
    useCreateContractGroupMutation,
    useAssignContractsToGroupMutation,
    useRemoveContractsFromGroupMutation,
    useDeleteContractGroupMutation,
    createContractGroup,
    assignContractsToGroup,
    removeContractsFromGroup,
    deleteContractGroup
  };
}
